import { streamChat } from "@/apis/cs-rag/route";
import { logInvectorAiQuestion } from "@/utils/usage-log";
import { useCallback, useEffect, useRef, useState } from "react";
import { buildHistory } from "./build-history";
import type { ChatMessage } from "./chat-message";
import { buildCitationIdMap } from "./use-parse-citations";

let msgSeq = 0;

const normalizeAnswer = (answer: string) => {
  const blocks = answer.split(/\n{2,}/);
  const dedupedBlocks: string[] = [];

  for (const block of blocks) {
    const previousBlock = dedupedBlocks[dedupedBlocks.length - 1];
    if (
      previousBlock &&
      normalizeBlock(previousBlock) === normalizeBlock(block)
    ) {
      continue;
    }

    dedupedBlocks.push(block);
  }

  return dedupedBlocks.join("\n\n");
};

const normalizeBlock = (block: string) => block.replace(/\s+/g, " ").trim();

export function useChat() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [activeMsgId, setActiveMsgId] = useState<string | null>(null);
  const [isPending, setIsPending] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  // delta 배치용 ref
  const pendingDeltaRef = useRef("");
  const rafRef = useRef(0);

  // send() 는 useCallback([]) 라 messages state 를 직접 읽지 못하므로,
  // 직전 대화 맥락(history) 조립을 위해 최신 messages 를 ref 로 미러링한다.
  // 메모리에만 보관 — localStorage/DB 저장 없음. 새로고침 시 소멸.
  const messagesRef = useRef<ChatMessage[]>([]);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // 컴포넌트 언마운트 시 스트림 정리
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const send = useCallback(async (question: string) => {
    logInvectorAiQuestion(question);
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    // 현재 질문을 추가하기 전(=직전까지의 완료된 대화)으로 history 를 만든다.
    const history = buildHistory(messagesRef.current);

    const userMsgId = `msg-${++msgSeq}`;
    const botMsgId = `msg-${++msgSeq}`;

    pendingDeltaRef.current = "";
    cancelAnimationFrame(rafRef.current);

    setMessages((prev) => [
      ...prev,
      { id: userMsgId, role: "user", content: question },
      { id: botMsgId, role: "bot", content: "", isStreaming: true },
    ]);
    setIsPending(true);

    /** 누적된 delta를 한 번에 state에 반영 */
    const flushDelta = () => {
      const text = pendingDeltaRef.current;
      if (!text) return;
      pendingDeltaRef.current = "";
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === botMsgId ? { ...msg, content: msg.content + text } : msg,
        ),
      );
    };

    try {
      for await (const event of streamChat(
        question,
        controller.signal,
        history,
      )) {
        if (event.type === "delta") {
          // ref에 누적, 프레임당 1회만 flush
          pendingDeltaRef.current += event.text;
          if (!rafRef.current) {
            rafRef.current = requestAnimationFrame(() => {
              rafRef.current = 0;
              flushDelta();
            });
          }
        } else if (event.type === "citations") {
          // citations 전에 잔여 delta 확정
          cancelAnimationFrame(rafRef.current);
          rafRef.current = 0;
          const remaining = pendingDeltaRef.current;
          pendingDeltaRef.current = "";

          const citationNs = event.citations.map((c) => c.n);
          const citationIdMap = buildCitationIdMap(citationNs);
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === botMsgId
                ? {
                    ...msg,
                    content: normalizeAnswer(msg.content + remaining),
                    citations: event.citations,
                    citationIdMap,
                    isStreaming: false,
                  }
                : msg,
            ),
          );
          setActiveMsgId(botMsgId);
          // citations는 마지막 이벤트 — 연결 종료
          break;
        } else if (event.type === "error") {
          cancelAnimationFrame(rafRef.current);
          rafRef.current = 0;
          const remaining = pendingDeltaRef.current;
          pendingDeltaRef.current = "";

          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === botMsgId
                ? {
                    ...msg,
                    content: msg.content + remaining,
                    isStreaming: false,
                    streamError: event.message,
                  }
                : msg,
            ),
          );
        }
      }

      // 스트림 종료 — citations 없이 끝난 경우 처리
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
      const remaining = pendingDeltaRef.current;
      pendingDeltaRef.current = "";

      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === botMsgId && msg.isStreaming
            ? {
                ...msg,
                isStreaming: false,
                content: normalizeAnswer(msg.content + remaining),
              }
            : msg,
        ),
      );
    } catch (e) {
      if (controller.signal.aborted) return;
      console.error("[cs-rag] streaming error:", e);

      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
      const remaining = pendingDeltaRef.current;
      pendingDeltaRef.current = "";

      const errorMessage =
        e instanceof Error ? e.message : "알 수 없는 오류가 발생했습니다";
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === botMsgId
            ? {
                ...msg,
                content: msg.content + remaining,
                isStreaming: false,
                streamError: errorMessage,
              }
            : msg,
        ),
      );
    } finally {
      if (!controller.signal.aborted) {
        setIsPending(false);
      }
    }
  }, []);

  const switchActiveCitations = useCallback(
    (msgId: string) => {
      if (msgId === activeMsgId) return;
      setActiveMsgId(msgId);
    },
    [activeMsgId],
  );

  const activeMessage = messages.find((m) => m.id === activeMsgId);

  return {
    messages,
    send,
    isPending,
    activeCitations: activeMessage?.citations ?? [],
    activeCitationIdMap: activeMessage?.citationIdMap ?? {},
    activeMsgId,
    switchActiveCitations,
  };
}
